import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { useGetProgramsQuery } from 'services/cms';
import { RootState } from 'store/index';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';
import { useDebounce } from 'utils/useDebounce';

import Section from 'components/Section';

type SearchVerticalListProps = {
  searchInputValue?: string;
  categoryValue?: ProgramType;
  containerWidth?: number | undefined;
  topContainerHeight?: number | undefined;
};
const SearchVerticalList: React.FC<SearchVerticalListProps> = ({
  categoryValue,
  searchInputValue,
  ...props
}) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const analytics = useAnalytics();

  const delayedValue = useDebounce(searchInputValue);

  const profile = useSelector((state: RootState) => state.profile);

  const { data, isLoading, isFetching, isSuccess } = useGetProgramsQuery({
    q: delayedValue,
    type: categoryValue,
    page,
  });

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMorePages(true);
    if (delayedValue) analytics.triggerSearchEvent(delayedValue);
  }, [delayedValue, categoryValue, profile.ageRating]);

  useEffect(() => {
    if (isFetching || !isSuccess) {
      return;
    }

    if (data?.items) {
      if (page === 1) {
        setItems([...data.items]);
      } else {
        setItems((oldItems) => {
          const newItems = data.items.filter(
            (newItem: any) => !oldItems.find((oldItem) => oldItem._id === newItem._id)
          );
          return [...oldItems, ...newItems];
        });
      }

      const lastFetchItemCount = data.items.length;
      const limitPerPage = data.meta?.limit || (Platform.isTV ? 8 : 10);
      setHasMorePages(lastFetchItemCount >= limitPerPage);
    } else {
      if (page === 1) {
        setItems([]);
      }
      setHasMorePages(false);
    }
  }, [data, isFetching, isSuccess]);

  const formattedData: CmsCard[] = useMemo(() => {
    return (
      items.map<CmsCard>((item) => ({
        type: item.type,
        title: item.title,
        image: item.imageVertical || item.image,
        link: `/${item.url}`,
        height: item.imageVertical?.height || item.image?.height,
        _id: item._id,
        id: item.id,
        search_term: delayedValue,
        search_category: categoryValue,
      })) || []
    );
  }, [items, delayedValue, categoryValue]);

  const HandleOnEndReached = useCallback(() => {
    if (!isFetching && hasMorePages) {
      setPage((p) => p + 1);
    }
  }, [isFetching, hasMorePages]);

  const showInitialLoading = isLoading && page === 1;
  const showPaginationLoading = isFetching && page > 1;

  if (showInitialLoading) {
    return (
      <View style={styles.showInitialLoadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Section
        onEndReached={HandleOnEndReached}
        vertical
        items={formattedData}
        _name="section"
        showTitle={false}
        loading={isLoading}
        variant="section.medium"
        title={searchInputValue || ''}
        {...props}
      />
      {showPaginationLoading && (
        <View style={styles.spinnerContainer}>
          <ActivityIndicator size="small" color="#0000ff" />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    flex: 1,
  },
  spinnerContainer: {
    position: 'absolute',
    width: '100%',
    top: 0,
    bottom: calcSizeForDevice(20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  showInitialLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default SearchVerticalList;
